/**
 * Split Modes Engine
 * Supports: per-item, equal, percentage, custom weight
 */

export const SPLIT_MODES = {
  ITEM:       'item',       // each person pays for what they ordered (default)
  EQUAL:      'equal',      // grand total / n
  PERCENTAGE: 'percentage', // host assigns % weights
  CUSTOM:     'custom',     // host assigns fixed amounts
};

/**
 * Equal split — everyone pays the same regardless of what they ordered
 */
export const calcEqualSplit = (grandTotal, participants) => {
  const share = parseFloat((grandTotal / Math.max(participants.length, 1)).toFixed(2));
  // Fix rounding: last person absorbs the cent diff
  const map = {};
  let assigned = 0;
  participants.forEach((p, i) => {
    const amt = i === participants.length - 1
      ? parseFloat((grandTotal - assigned).toFixed(2))
      : share;
    map[p.id] = amt;
    assigned += share;
  });
  return map;
};

/**
 * Percentage split — weights must sum to ~100
 */
export const calcPercentageSplit = (grandTotal, participants, weights) => {
  const map = {};
  let assigned = 0;
  participants.forEach((p, i) => {
    const pct = weights[p.id] ?? (100 / participants.length);
    const amt = i === participants.length - 1
      ? parseFloat((grandTotal - assigned).toFixed(2))
      : parseFloat(((pct / 100) * grandTotal).toFixed(2));
    map[p.id] = amt;
    assigned += amt;
  });
  return map;
};

/**
 * Item-based split (default) — per calcPersonBreakdown
 */
export const calcItemSplit = (items, claims, participants, subtotal, taxRate, tipPct) => {
  const { calcPersonBreakdown } = require('./calculations.js');
  const map = {};
  participants.forEach((p) => {
    const { total } = calcPersonBreakdown(items, claims, p.id, subtotal, taxRate, tipPct);
    map[p.id] = total;
  });
  return map;
};

/**
 * Validate that weights sum to 100 (within tolerance)
 */
export const validateWeights = (weights) => {
  const sum = Object.values(weights).reduce((a, b) => a + b, 0);
  return Math.abs(sum - 100) < 0.5;
};

/**
 * Distribute unclaimed items equally among all participants
 */
export const distributeUnclaimed = (items, claims, participants) => {
  const unclaimed = items.filter((i) => !(claims[i.id]?.length));
  if (!unclaimed.length || !participants.length) return claims;

  const next = { ...claims };
  unclaimed.forEach((item) => {
    next[item.id] = participants.map((p) => p.id);
  });
  return next;
};
